import { Theme } from '@mui/material';
import { GeoJSONSource, GeoJSONSourceRaw } from '@trimblemaps/trimblemaps-js';

import { NetworkArea } from '../../../interfaces/services/areas';
import { ValueReferences } from '../../enums';
import { formatNumber, formatNumberPercent } from '../../formattingUtils';
import { getGradeColor, getGradeStrokeColor } from '../../styleUtils';
import { FeatureTypes, MapSources } from '../enums';

/** Interface used for the properties object on the EmptyMiles Areas Map Source. */
export interface EmptyMilesAreasMapSourceProperties {
    id: string;
    featureType: FeatureTypes;
    color: string;
    strokeColor?: string;
    description: string;
}

/**
 * Loop through the areas and add a point feature to the source for each area location.
 * Adds the empty miles area source to the map if it doesn't already exist
 * @param map Instance of your map
 * @param areas The empty miles areas to show on the map
 * @param theme MUI theme to use for styling
 * @returns A geojson source to be added to the map
*/
export const addEmptyMilesAreasSource = (map: TrimbleMaps.Map, areas: NetworkArea[], theme: Theme): GeoJSONSourceRaw => {
    const features: GeoJSON.Feature<GeoJSON.Point | GeoJSON.LineString, EmptyMilesAreasMapSourceProperties>[] = [];

    areas.forEach((area): void => {
        if (area.average_empty_miles_percentage_grade !== null) {
            area.empty_miles_bottom_outbound_lanes.forEach((lane) => {
                if (area.service_area_longitude_value && area.service_area_latitude_value &&
                    lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
                    const laneColor = getGradeColor(lane.average_empty_miles_percentage_grade, theme);
                    const laneAreaTooltipHtml = `
                        <div class='mapTooltip'>
                            <div class='mapTooltipRow'>
                                <div class='mapTooltipCell'><strong>Origin:</strong></div>
                                <div class='mapTooltipCell'><strong>${area.service_area_name}</strong></div>
                            </div>
                            <div class='mapTooltipRow'>
                                <div class='mapTooltipCell'><strong>Destination:</strong></div>
                                <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                            </div>
                            <div class='mapTooltipRow'>
                                <div class='mapTooltipCell'>Empty Miles Grade:</div>
                                <div class='mapTooltipCell' style='color: ${laneColor};'>${lane.average_empty_miles_percentage_grade || ValueReferences.NA}</div>
                            </div>
                            <div class='mapTooltipRow'>
                                <div class='mapTooltipCell'>Avg. Empty Mile %:</div>
                                <div class='mapTooltipCell'>${formatNumberPercent(lane.average_empty_miles_percentage_amount, 2, 2)}</div>
                            </div>
                        </div>
                    `;

                    features.push({
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: [
                                [area.service_area_longitude_value, area.service_area_latitude_value],
                                [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                            ],
                        },
                        properties: {
                            id: lane.delivery_service_area_id,
                            featureType: FeatureTypes.AreaLaneLine,
                            color: laneColor,
                            description: laneAreaTooltipHtml,
                        },
                    });
                }

                if (lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
                    features.push({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                        },
                        properties: {
                            id: lane.delivery_service_area_id,
                            featureType: FeatureTypes.AreaLaneCenter,
                            color: theme.palette.grey[300],
                            description: '',
                        },
                    });
                }
            });

            if (area.service_area_longitude_value && area.service_area_latitude_value) {
                const areaColor = getGradeColor(area.average_empty_miles_percentage_grade, theme);
                const areaTooltipHtml = `
                    <div class='mapTooltip'>
                        <div class='mapTooltipRow'>
                            <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                            <div class='mapTooltipCell'><strong>${area.service_area_name}</strong></div>
                        </div>
                        <div class='mapTooltipRow'>
                            <div class='mapTooltipCell'>Area Id:</div>
                            <div class='mapTooltipCell'>${area.service_area_id}</div>
                        </div>
                        <div class='mapTooltipRow'>
                            <div class='mapTooltipCell'>Empty Miles Grade:</div>
                            <div class='mapTooltipCell' style='color: ${areaColor};'>${area.average_empty_miles_percentage_grade || ValueReferences.NA}</div>
                        </div>
                        <div class='mapTooltipRow'>
                            <div class='mapTooltipCell'>Avg. Empty Miles:</div>
                            <div class='mapTooltipCell'>${formatNumber(area.average_empty_miles_percentage_amount, 2, 2)}</div>
                        </div>
                    </div>
                `;

                features.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [area.service_area_longitude_value, area.service_area_latitude_value],
                    },
                    properties: {
                        id: area.service_area_id,
                        featureType: FeatureTypes.AreaCenter,
                        color: areaColor,
                        strokeColor: getGradeStrokeColor(area.average_empty_miles_percentage_grade, theme),
                        description: areaTooltipHtml,
                    },
                });
            }
        }
    });

    const source: GeoJSONSourceRaw = {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features,
        },
    };

    if (map.getSource(MapSources.emptyMilesAreasSource) === undefined) {
        map.addSource(MapSources.emptyMilesAreasSource, source);
    } else if (source.data) {
        (map.getSource(MapSources.emptyMilesAreasSource) as GeoJSONSource).setData(source.data);
    }

    return source;
};
